import React, { useState, useEffect } from "react";
import axios from "axios";

function Weather() {
  const [weather, setWeather] = useState(null);
  const [temperaturePref, setTemperaturePref] = useState("both");
  const [currentCity, setCurrentCity] = useState("Windsor");

  const cities = {
    Detroit: { lat: 42.3314, lon: -83.0458, name: "Detroit" },
    Windsor: { lat: 42.3149, lon: -83.0364, name: "Windsor" },
  };

  const getWeatherDescription = (code) => {
    const descriptions = {
      0: "Clear sky",
      1: "Mainly clear",
      2: "Partly cloudy",
      3: "Overcast",
      45: "Fog",
      48: "Depositing rime fog",
      51: "Light drizzle",
      53: "Moderate drizzle",
      55: "Dense drizzle",
      56: "Light freezing drizzle",
      57: "Dense freezing drizzle",
      61: "Slight rain",
      63: "Moderate rain",
      65: "Heavy rain",
      66: "Light freezing rain",
      67: "Heavy freezing rain",
      71: "Slight snow fall",
      73: "Moderate snow fall",
      75: "Heavy snow fall",
      77: "Snow grains",
      80: "Slight rain showers",
      81: "Moderate rain showers",
      82: "Violent rain showers",
      85: "Slight snow showers",
      86: "Heavy snow showers",
      95: "Thunderstorm",
      96: "Thunderstorm with slight hail",
      99: "Thunderstorm with heavy hail",
    };
    return descriptions[code] || "Unknown";
  };

  const getWeatherEmoji = (code, isDay) => {
    if (code === 0) return isDay ? "☀️" : "🌙"; // Clear sky: sun or moon
    if (code <= 3) return "☁️"; // Cloudy
    if (code >= 45 && code <= 48) return "🌫️"; // Fog
    if (code >= 51 && code <= 67) return "🌧️"; // Rain
    if (code >= 71 && code <= 86) return "❄️"; // Snow
    if (code >= 95) return "⛈️"; // Thunderstorm
    return "🌤️"; // Default
  };

  useEffect(() => {
    // Load preferences from localStorage on mount
    setTemperaturePref(localStorage.getItem("temperature") || "both");
    setCurrentCity(localStorage.getItem("weatherLocation") || "Windsor");
  }, []);

  useEffect(() => {
    // Fetch weather whenever currentCity changes
    const fetchWeather = async () => {
      try {
        const city = cities[currentCity];
        const response = await axios.get(
          `https://api.open-meteo.com/v1/forecast?latitude=${city.lat}&longitude=${city.lon}&current_weather=true&temperature_unit=celsius&hourly=temperature_2m,weathercode&forecast_hours=6&timezone=America%2FNew_York`,
        );
        setWeather({
          main: { temp: response.data.current_weather.temperature },
          weather: [
            {
              description: getWeatherDescription(
                response.data.current_weather.weathercode,
              ),
              code: response.data.current_weather.weathercode,
              isDay: response.data.current_weather.is_day,
            },
          ],
          hourly: response.data.hourly,
        });
      } catch (error) {
        console.error("Error fetching weather:", error);
      }
    };
    fetchWeather();
    if (window.gtag && window.consented)
      window.gtag("event", "tool_view", { tool: "weather" });
  }, [currentCity]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleCity = () => {
    const newCity = currentCity === "Detroit" ? "Windsor" : "Detroit";
    setCurrentCity(newCity);
    localStorage.setItem("weatherLocation", newCity);
    if (window.gtag && window.consented)
      window.gtag("event", "weather_city_toggle", {
        from_city: currentCity,
        to_city: newCity,
      });
  };

  const tempC = weather ? weather.main.temp.toFixed(1) : "Loading...";
  const tempF = weather
    ? ((weather.main.temp * 9) / 5 + 32).toFixed(1)
    : "Loading...";

  return (
    <section
      className="utility weather-section canada-theme us-theme"
      onClick={toggleCity}
      style={{ cursor: "pointer" }}
    >
      <h2>
        Weather in {currentCity}{" "}
        <span style={{ fontSize: "0.8em", opacity: 0.7 }}>
          (Click to switch)
        </span>
      </h2>
      <div className="weather-current">
        <span className="temp">
          {temperaturePref === "c"
            ? `${tempC}°C`
            : temperaturePref === "f"
              ? `${tempF}°F`
              : `${tempC}°C / ${tempF}°F`}
        </span>
        <span className="icon">
          {weather
            ? getWeatherEmoji(weather.weather[0].code, weather.weather[0].isDay)
            : ""}
        </span>
      </div>
      {weather && weather.hourly && (
        <div className="hourly-forecast">
          {weather.hourly.time.slice(1, 6).map((time, index) => {
            const hourTempC = weather.hourly.temperature_2m[index + 1];
            const hourTempF = (hourTempC * 9) / 5 + 32;
            const hourCode = weather.hourly.weathercode[index + 1];
            const hour = new Date(time).toLocaleTimeString("en-US", {
              hour: "numeric",
              hour12: true,
            });
            return (
              <div key={index} className="hour-item">
                <div>{hour}</div>
                <div>{getWeatherEmoji(hourCode, true)}</div>
                <div>
                  {temperaturePref === "c"
                    ? `${hourTempC.toFixed(0)}°C`
                    : temperaturePref === "f"
                      ? `${hourTempF.toFixed(0)}°F`
                      : `${hourTempC.toFixed(0)}°C / ${hourTempF.toFixed(0)}°F`}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default Weather;
